'use client';

import { KycWithDocumentNames, TKycWithDocumentNamesRows } from '@verifime/components';
import { ReactElement } from 'react';

export type TKycWithNamesProps = {
  /**
   * The rows to render, one per identity document the customer holds.
   *
   * **A row, not the raw document.** Turning a customer's identity documents into rows means
   * knowing about the API's own document shapes, the structured-versus-legacy name fields among
   * them, and how to compose a name out of either: that is a views concern (ADR 50/62), not this
   * component's. This component stays dumb and presentational, so it takes the already-built row
   * and knows nothing about where it came from. `documentRows` in `@verifime/views` is what builds
   * it, from a customer's documents.
   */
  rows: TKycWithDocumentNamesRows;
  /** Draws placeholder rows while the customer is being fetched. See the component's own prop. */
  isLoading?: boolean;
  'data-testid'?: string;
};

/**
 * The KYC table with the name each document printed.
 *
 * **The name column is the point of this component.** The old table showed which documents a
 * customer holds; this one also shows what each one says their name is, which is what lets a client
 * see that a licence and a passport disagree.
 *
 * The columns are fixed here rather than taken as a prop. Every caller wants the same five, and a
 * caller free to choose could omit `extractedName`, which would silently turn this back into the old
 * table while still looking like the new one.
 *
 * **A component, not a view.** It was briefly in `@verifime/views` and did not belong there: it
 * makes no API call, and per ADR 62 a View Component is one that is API endpoint specific and
 * handles the request itself. Everything here arrives as props, including the rows themselves: the
 * caller builds them from a customer's documents and hands the result in, so this file never needs
 * to know what a licence or a passport looks like.
 */
export default function KycWithNames({
  rows,
  isLoading,
  'data-testid': dataTestId,
}: Readonly<TKycWithNamesProps>): ReactElement {
  return (
    <KycWithDocumentNames
      rows={rows}
      columnNames={['documentType', 'extractedName', 'expires', 'countryOfIssue', 'lastUpdated']}
      isLoading={isLoading}
      data-testid={dataTestId}
    />
  );
}
